import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const COPY = {
  it: {
    kicker: "Primo passo · 20 secondi",
    title: "Facciamo subito il tuo primo preventivo.",
    sub: "Non devi compilare campi. Racconta un lavoro che fai davvero, come lo diresti a un collega: Operativo scrive voci, quantità, prezzi e IVA.",
    placeholder: "es. Rifacimento impianto elettrico cucina di un ristorante, 3 giorni, materiali inclusi, cliente Trattoria Bianchi",
    examplesTitle: "Oppure parti da un esempio:",
    examples: [
      "Ristrutturazione bagno 6 mq, 10 giorni, cliente Rossi",
      "Manutenzione caldaia condominio, 12 appartamenti, contratto annuale",
      "Sito web vetrina 5 pagine con fotografie, cliente Studio Neri",
    ],
    cta: "Crea il mio preventivo",
    working: "Sto scrivendo il preventivo…",
    skip: "Lo faccio dopo, portami alla dashboard",
    hint: "Puoi correggere ogni riga dopo: nulla viene inviato al cliente adesso.",
    tooShort: "Scrivi qualche parola in più sul lavoro (almeno 15 caratteri).",
    empty: "Non sono riuscito a leggere il brief. Prova a descrivere il lavoro in una frase.",
    done: "Ecco il tuo primo preventivo.",
  },
  en: {
    kicker: "First step · 20 seconds",
    title: "Let's make your first quote right now.",
    sub: "No fields to fill in. Describe a job you actually do, the way you'd tell a colleague: Operativo writes the line items, quantities, prices and tax.",
    placeholder: "e.g. Rewire a restaurant kitchen, 3 days, materials included, client Bianchi Bistro",
    examplesTitle: "Or start from an example:",
    examples: [
      "Bathroom renovation 6 sqm, 10 days, client Miller",
      "Boiler maintenance for a 12-flat building, yearly contract",
      "5-page brochure website with photography, client Neri Studio",
    ],
    cta: "Create my quote",
    working: "Writing your quote…",
    skip: "I'll do it later, take me to the dashboard",
    hint: "You can edit every line afterwards: nothing is sent to your client now.",
    tooShort: "Add a few more words about the job (at least 15 characters).",
    empty: "I couldn't read that brief. Try describing the job in one sentence.",
    done: "Here is your first quote.",
  },
};

const Benvenuto = () => {
  const { lang } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const c = COPY[lang] || COPY.en;

  const finish = () => {
    api.post("/auth/onboarded").catch(() => {});
    if (user) setUser({ ...user, onboarded_at: new Date().toISOString() });
  };

  const skip = () => {
    finish();
    navigate("/dashboard", { replace: true });
  };

  const create = async () => {
    if (brief.trim().length < 15) return toast.error(c.tooShort);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/draft-document", { brief, type: "quote" });
      if (!data.items?.length) {
        toast.error(c.empty);
        return;
      }
      const { data: doc } = await api.post("/documents", {
        type: "quote",
        client_name: data.client_name || "",
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: "",
        tax_rate: data.tax_rate ?? 22,
        currency: data.currency || "EUR",
        notes: data.notes || "",
        status: "bozza",
        items: data.items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 1,
          unit_price: Number(i.unit_price) || 0,
        })),
      });
      finish();
      toast.success(c.done);
      navigate(`/documenti/${doc.id}`, { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-6 py-16 text-slate-100 lg:px-16" data-testid="onboarding-page">
      <div className="mx-auto max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-300">
          <Sparkles className="h-3 w-3" /> {c.kicker}
        </span>
        <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl">{c.title}</h1>
        <p className="mt-5 text-base leading-relaxed text-slate-400">{c.sub}</p>

        <Textarea
          data-testid="onboarding-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={c.placeholder}
          rows={4}
          className="mt-8 border-slate-700 bg-slate-900 text-base text-slate-100 placeholder:text-slate-500 focus:border-indigo-500"
        />

        <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {c.examplesTitle}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {c.examples.map((ex, i) => (
            <button
              key={ex}
              type="button"
              data-testid={`onboarding-example-${i + 1}`}
              onClick={() => setBrief(ex)}
              className="rounded-full border border-slate-700 px-4 py-2 text-left text-xs text-slate-300 transition-colors hover:border-indigo-500 hover:text-white"
            >
              {ex}
            </button>
          ))}
        </div>

        <Button
          data-testid="onboarding-submit"
          onClick={create}
          disabled={loading}
          className="mt-9 w-full gap-2 bg-indigo-600 py-7 text-base text-white hover:bg-indigo-500 sm:w-auto sm:px-10"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? c.working : c.cta}
        </Button>
        <p className="mt-4 text-xs text-slate-500">{c.hint}</p>

        <button
          type="button"
          data-testid="onboarding-skip"
          onClick={skip}
          className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 underline-offset-4 transition-colors hover:text-slate-200 hover:underline"
        >
          {c.skip} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default Benvenuto;
